import { Grid, GridColumn, IFormHelperProps, Notice, Spinner, Text, TextField } from 'telia-front-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import LoginButton from '../../components/LoginButton';
import PasskeyOfferRegister from '../../components/PasskeyOfferRegister';
import {
  PERSONAL_CODE_FIELD_ID,
  PHONE_FIELD_ID,
  getHelperErrorMessage,
  helperInErrorState,
  personalCodeHelper,
  phoneFieldHelper,
  validatePersonalCode,
} from '../../util/FieldHelperUtil';
import { runInTeliaRumService } from '../../util/TeliaOtelUtil';
import { isPhoneValid } from '../../util/ValidationUtil';
import RememberMe from '../RememberMe';
import PendingLogin from './PendingLogin';
import PollableLoginStatusNotice from './PollableLoginStatusNotice';
import useStores from '../../stores/UseStores';
import ServiceStatusNotice from './ServiceStatusNotice';
import { TestAuthWarning } from './TestAuthWarning';

const MobileIdLogin = observer((): React.JSX.Element => {
  const {
    mobileIdLoginStore,
    translateStore: { translate },
    uiStore: { getLoginPanelGridWidth, showErrorNoticeWithErroredFields, setSelectedTab },
    passkeyStore,
  } = useStores();
  const { loginCredentials, verificationCode, status, startLogin } = mobileIdLoginStore;

  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);
  const [numberHelper, setNumberHelper] = React.useState<IFormHelperProps | undefined>(undefined);
  const [perCodeHelper, setPerCodeHelper] = React.useState<IFormHelperProps | undefined>(undefined);

  const getPhoneFieldHelper = (forceIsDirty?: boolean) => phoneFieldHelper(translate, loginCredentials.mobileNumber, forceIsDirty ?? hasSubmitted);
  const getPersonalCodeHelper = (forceIsDirty?: boolean) => personalCodeHelper(translate, loginCredentials.personalCode, 'EE', forceIsDirty ?? hasSubmitted);

  React.useEffect(() => {
    setNumberHelper(getPhoneFieldHelper());
  }, [loginCredentials.mobileNumber, hasSubmitted]);

  React.useEffect(() => {
    setPerCodeHelper(getPersonalCodeHelper());
  }, [loginCredentials.personalCode, hasSubmitted]);

  React.useEffect(() => {
    mobileIdLoginStore.fetchServiceStatus();
  }, []);

  const login = (event: React.FormEvent) => {
    runInTeliaRumService('Autentimise algatamine (MID)', () => {
      event.preventDefault();
      setHasSubmitted(true);
      if (!isInvalid()) {
        startLogin();
      } else {
        showErrorNoticeWithErroredFields('form.error', [getPhoneFieldHelper(true), getPersonalCodeHelper(true)]);
      }
    });
  };

  const isInvalid = () => {
    return !validatePersonalCode(loginCredentials.personalCode) || !isPhoneValid(loginCredentials.mobileNumber.replace('+', ''));
  };

  if (passkeyStore.offerRegisterModalVisible) {
    return <PasskeyOfferRegister />;
  }

  if (status === 'REDIRECTING' || status === 'SUCCESS') {
    return <Spinner />;
  }

  return (
    <>
      <span className="sr-only" aria-live="polite">
        {status === 'PENDING' && `${translate('mid.instruction')} ${verificationCode?.split('').join(' ')}`}
      </span>
      {status === 'PENDING' ? (
        <PendingLogin method={'mid'} pollableLoginStore={mobileIdLoginStore} />
      ) : (
        <>
          <ServiceStatusNotice pollableLoginStore={mobileIdLoginStore} />
          <form onSubmit={(event) => login(event)} id="mobileIdLogin">
            <Text>
              <PollableLoginStatusNotice pollableLoginStore={mobileIdLoginStore} />
              <Grid align={['center-sm']}>
                <GridColumn width={['sm-10', 'md-8', 'lg-8']}>
                  <TestAuthWarning method="mid" />
                </GridColumn>
              </Grid>
              <Grid align={['center-sm']}>
                {mobileIdLoginStore.shouldSavePasskeyAfterLogin && (
                  <GridColumn width={getLoginPanelGridWidth()}>
                    <Notice type="info">{translate('passkey.save.after.login.notice')}</Notice>
                  </GridColumn>
                )}
                <GridColumn width={getLoginPanelGridWidth()}>
                  <Text>
                    <TextField
                      label={translate('panel.personalCode')}
                      id={PERSONAL_CODE_FIELD_ID}
                      value={loginCredentials.personalCode}
                      onChange={(value) => (loginCredentials.personalCode = value)}
                      isInvalid={helperInErrorState(perCodeHelper)}
                      input={{ inputMode: 'numeric' }}
                      errorMessage={getHelperErrorMessage(perCodeHelper)}
                      required={true}
                    />
                    <TextField
                      label={translate('panel.mid.text')}
                      id={PHONE_FIELD_ID}
                      value={loginCredentials.mobileNumber}
                      onChange={(value) => (loginCredentials.mobileNumber = value)}
                      isInvalid={helperInErrorState(numberHelper)}
                      input={{ inputMode: 'tel', autoComplete: 'tel' }}
                      errorMessage={getHelperErrorMessage(numberHelper)}
                      required={true}
                    />
                    <RememberMe tabItemId="mobileIdLoginTab" />
                    <LoginButton isLoading={status === 'SUBMITTING'} disabled={status === 'SUBMITTING'} onClick={() => setSelectedTab('mobileId')} />
                  </Text>
                </GridColumn>
              </Grid>
            </Text>
          </form>
        </>
      )}
    </>
  );
});

export default MobileIdLogin;
